import { useEffect, useRef, useState } from 'react';

import { ensureMwMap } from './vendor.js';
import type { MwMapRecord } from '../model/mw-map-records.js';

/**
 * The typed React wrapper over `vendor/maps/mw-map.js` (M5-D7).
 *
 * Props in → `api` calls; `CustomEvent`s out → callbacks. Nothing else: the
 * renderer is vendored verbatim and this file is the only thing between it and
 * React, which is what keeps a re-vendor a clean diff.
 *
 * The imperative surface is two calls and one event:
 *
 * ```
 * el.api.setEvents(recs)          // the display records — model/mw-map-records.ts
 * el.api.setClock(seconds, run)   // corrected server time, its own channel
 * el.addEventListener('bs-mw-select', …)
 * ```
 *
 * The clock rides separately **because the renderer says so**: a track's position
 * changes every frame, and rebuilding the whole warning layer once a second to
 * move six chevrons is both wasteful and visibly steppy. So `setEvents` fires
 * only when the records actually change, and `setClock` fires on every tick.
 *
 * ## The thirty-fold cost, found and fixed — it was this file
 *
 * `MW_MAP_ENABLED` shipped false because mounting the element made the warning
 * board roughly **thirty times slower to interact with**. The handoff's leading
 * hypothesis was the `regionNames` override in `mw-map.js` defeating the base's
 * rank-3 budget. **Profiled, and it is not that**: across a 24 s zoom drag
 * `regionNames` cost 57 ms of the whole trace, and First Contact declares four
 * regions, so the placement search it was blamed for cannot be the bill.
 *
 * The cost was the mount effect below. `onSelect` sat in its dependency array and
 * `MwView` passes an inline arrow, so every render of the pane destroyed the
 * `<mw-map>` element and built a new one — and a new element is a `boot()`, a
 * `resize()`, a `buildRings()`, a `geometry()` (polylabel over every ring) and a
 * `drawBase()` that reprojects the whole world. With `useServerNow` ticking twice
 * a second that ran with nobody touching anything:
 *
 * | 5 s idle, no input | before | after |
 * | --- | --- | --- |
 * | element `boot()` | 9 | 1 |
 * | `drawBase()` — world reprojected | 16 | 1 |
 *
 * The renderer was never at fault, which is why the theatre board was fast with
 * the same base class: `maps/RegionMap.tsx` reads its callbacks through a ref and
 * this file did not. `vendor/maps/mw-map.js` is unchanged.
 *
 * ## It is still off, and the reason is now a layout question
 *
 * What remains is not performance. The pane was re-authored around the map's
 * absence (2026-09-04): flipping this constant returns the rail to 232 px, gives
 * the pane to the map and sends the reading back to the right rail, where at the
 * 1280 × 720 floor 623 px of 826 px sat below a 203 px fold and the WARNING
 * CREDIT ladder was clipped to its header strip from stage 0. `docs/handoff-mw-map.md`
 * §5 holds the three options. **The measurement no longer decides this; the
 * layout does.**
 */

/** The subset of the vendored element's surface this wrapper drives. */
interface MwMapElement extends HTMLElement {
  api?: {
    setEvents?: (records: readonly MwMapRecord[]) => void;
    setClock?: (seconds: number, running: boolean) => void;
    focusEvent?: (id: string) => void;
  };
}

export interface MwMapProps {
  readonly records: readonly MwMapRecord[];
  /** Seconds since the orders window opened, corrected (§8.2). */
  readonly clockSeconds: number;
  readonly running: boolean;
  readonly onSelect: (eventId: string | null) => void;
}

const MW_MAP_TAG = 'mw-map';

/**
 * Whether the warning board mounts its map.
 *
 * Not a feature flag and not configuration: a named switch whose reason is in the
 * header, so the next person meets it before the code. **The reason has changed.**
 * The thirty-fold cost that put it here is fixed and measured (1.5 s against the
 * 38.3 s it replaced, on `mw-staged-reveal`'s pre-release sequence with the map
 * mounted). What holds it false now is the layout question in
 * `docs/handoff-mw-map.md` §5 — flipping it moves the reading back into a 203 px
 * rail that cannot hold it — and that is somebody's decision, not this constant's.
 */
export const MW_MAP_ENABLED = false;

/** Has the vendored renderer defined itself, and may it draw? */
function mwMapAvailable(): boolean {
  if (!MW_MAP_ENABLED) return false;
  return typeof customElements !== 'undefined' && customElements.get(MW_MAP_TAG) !== undefined;
}

/**
 * The same answer, for a caller that has to *lay out* around it.
 *
 * Registration is deferred and asynchronous (`maps/vendor.ts`), so the honest
 * answer changes once after mount and the layout has to re-render when it does.
 *
 * With the switch off there is nothing to wait for and nothing that can change
 * it, so the registration chain — which imports d3, topojson and milsymbol, and
 * evaluates two vendored renderers — is not started at all. Every seat mounts
 * this hook through `ConsoleShell`, not just the MW one.
 */
export function useMwMapAvailable(): boolean {
  const [available, setAvailable] = useState(mwMapAvailable);
  useEffect(() => {
    if (!MW_MAP_ENABLED) return;
    let live = true;
    void ensureMwMap().then(() => {
      if (live) setAvailable(mwMapAvailable());
    });
    return () => {
      live = false;
    };
  }, []);
  return available;
}

export function MwMap(props: MwMapProps): JSX.Element {
  const hostRef = useRef<HTMLDivElement>(null);
  const elementRef = useRef<MwMapElement | null>(null);
  const available = useMwMapAvailable();

  // Callbacks are read through a ref so that a parent re-rendering with new
  // closures does not tear the element down and rebuild it. `maps/RegionMap.tsx`
  // has carried this since it was written and this file did not copy it, which
  // is the whole of the thirty-fold cost in the header: `MwView` passes an inline
  // arrow, so `onSelect` was a new identity on every render, and a `useServerNow`
  // that ticks twice a second was enough to make the mount effect a treadmill.
  const handlers = useRef(props);
  handlers.current = props;

  useEffect(() => {
    const host = hostRef.current;
    if (host === null || !available) return;
    const element = document.createElement(MW_MAP_TAG) as MwMapElement;
    element.style.width = '100%';
    element.style.height = '100%';
    const onMwSelect = (event: Event): void => {
      const detail = (event as CustomEvent<string | null>).detail;
      handlers.current.onSelect(detail ?? null);
    };
    element.addEventListener('bs-mw-select', onMwSelect);
    host.appendChild(element);
    elementRef.current = element;
    return () => {
      element.removeEventListener('bs-mw-select', onMwSelect);
      element.remove();
      elementRef.current = null;
    };
  }, [available]);

  // `boot()` is async inside the element, so `api` appears a frame or two after
  // the node does. Both effects therefore check for it every time rather than
  // capturing it once — and both are no-ops until it is there, which is exactly
  // the behaviour the design's own bridge has.
  const records = props.records;
  useEffect(() => {
    elementRef.current?.api?.setEvents?.(records);
  }, [records]);

  const { clockSeconds, running } = props;
  useEffect(() => {
    elementRef.current?.api?.setClock?.(clockSeconds, running);
  }, [clockSeconds, running]);

  // No absent state here. `MwView` asks `useMwMapAvailable()` itself and draws the
  // reading instead of mounting this component, so a notice on this side could
  // never render — and the one that used to be here said `NO MAP PACK FOR THIS
  // SCENARIO`, which would have sent somebody to write a pack that already exists
  // (M5-D11). The absence has one owner and it is the pane that lays out around it.
  return (
    <div
      ref={hostRef}
      data-testid="mw-map"
      style={{ flex: 1, minHeight: 0, position: 'relative', background: 'var(--canvas)' }}
    />
  );
}
